import { Address } from '@adornis/address/db/address.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { registerEnum } from '@adornis/baseql/metadata/register.js';
import { validate } from '@adornis/validation/decorators.js';
import { MultipleValidationError } from '@adornis/validation/errors/MultipleValidationError.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { and } from '@adornis/validation/functions/and.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { UserStammdaten } from '../../entities/stammdaten.js';
import type { RegistrationStammdatenPlugin } from '../plugin-types.js';

export const ikhRegistrationStammdatenPlugin: RegistrationStammdatenPlugin = {
  id: 'registration::stammdaten::ikh',
  type: 'registration::stammdaten',
  disableCustomFormfields: true,
  createStammdaten: async ({ userID, institutionID, createInDB } = {}) => {
    const stammdaten = new IKHStammdaten({ userID, institutionID });
    if (createInDB) await stammdaten.create();
    return stammdaten;
  },
  customValidate: (stammdaten: IKHStammdaten) => {
    const ad = stammdaten.address;
    if (stammdaten.invoiceReceiver === IKHInvoiceReceiverEnum.IKHPRIVATE && ad?.zipCity?.city === 'Frankfurt') {
      for (const place of ['Ginnheimer Landstraße 39', 'Senckenberganlage 31', 'Theodor-Stern-Kai 7']) {
        if (ad.streetAndHousenumber === place)
          return 'Private Rechnungen können nicht an die Universität Frankfurt gestellt werden';
      }

      for (const placePrefix of [
        'Theodor-W.-Adorno-Platz',
        'Grüneburgplatz',
        'Norbert-Wollheim-Platz',
        'Max-von-Laue-Strasse',
      ]) {
        if (ad.streetAndHousenumber?.startsWith(placePrefix))
          return 'Private Rechnungen können nicht an die Universität Frankfurt gestellt werden';
      }
    }
    return undefined;
  },
  getFields: () => {
    return Object.keys(IKHStammdaten.fields).filter(it => !['_id', '_class', 'userID', 'institutionID'].includes(it));
  },
  classType: () => IKHStammdaten,
};

export enum IKHSalutationEnum {
  IKHFRAU = 'IKHFRAU',
  IKHHERR = 'IKHHERR',
  IKHSONSTIGES = 'IKHSONSTIGES',
}
registerEnum({ enum: IKHSalutationEnum, name: 'IKHSalutationEnum' });

export enum IKHOccupationEnum {
  IKHPROF_GOETHE = 'IKHPROF_GOETHE',
  IKHSINCE_GOETHE = 'IKHSINCE_GOETHE',
  IKHTEACHER_GOETHE = 'IKHTEACHER_GOETHE',
  IKHHIWI_GOETHE = 'IKHHIWI_GOETHE',
  IKHEMPLOYEE_GOETHE = 'IKHEMPLOYEE_GOETHE',
  IKHTEACHER = 'IKHTEACHER',
  IKHHESS_HOCHSCHULANGEHOERIGE = 'IKHHESS_HOCHSCHULANGEHOERIGE',
  IKHSONSTIGE = 'IKHSONSTIGE',
}
registerEnum({ enum: IKHOccupationEnum, name: 'IKHOccupationEnum' });

export enum IKHInstitutionEnum {
  IKHRECHT = 'IKHRECHT',
  IKHWIRTSCHAFT = 'IKHWIRTSCHAFT',
  IKHGESELLSCHAFT = 'IKHGESELLSCHAFT',
  IKHERZIEHUNG = 'IKHERZIEHUNG',
  IKHPSYCHOLOGIE = 'IKHPSYCHOLOGIE',
  IKHEVANG_THEOLOGIE = 'IKHEVANG_THEOLOGIE',
  IKHKATH_THEOLOGIE = 'IKHKATH_THEOLOGIE',
  IKHPHILOSOPHIE = 'IKHPHILOSOPHIE',
  IKHSPRACHEN = 'IKHSPRACHEN',
  IKHNEU_PHILOLOGIE = 'IKHNEU_PHILOLOGIE',
  IKHGEO = 'IKHGEO',
  IKHINFORMATIK = 'IKHINFORMATIK',
  IKHPHYSIK = 'IKHPHYSIK',
  IKHCHEMIE = 'IKHCHEMIE',
  IKHBIOLOGIE = 'IKHBIOLOGIE',
  IKHMEDIZIN = 'IKHMEDIZIN',
  IKHEXTERN = 'IKHEXTERN',
  IKHZENTRALE = 'IKHZENTRALE',
  IKHOTHERS = 'IKHOTHERS',
}
registerEnum({ enum: IKHInstitutionEnum, name: 'IKHInstitutionEnum' });

export enum IKHInvoiceReceiverEnum {
  IKHINTERNAL = 'IKHINTERNAL',
  IKHEXTERNAL = 'IKHEXTERNAL',
  IKHPRIVATE = 'IKHPRIVATE',
}
registerEnum({ enum: IKHInvoiceReceiverEnum, name: 'IKHInvoiceReceiverEnum' });

@Entity()
export class IKHStammdaten extends UserStammdaten {
  static override _class = 'IKHStammdaten';

  override getTranslationNamespace() {
    return 'ikh';
  }

  @Field(type => IKHSalutationEnum)
  salutation: Maybe<IKHSalutationEnum>;

  @Field(type => String)
  title: Maybe<string>;

  @validate(and(nonOptional()))
  @Field(type => String)
  phoneNumber: Maybe<string>;

  @Field(type => IKHInstitutionEnum)
  department: Maybe<IKHInstitutionEnum>;

  /**
   * only applicable for external department
   */
  @Field(type => String)
  externalDepartmentUniversity: Maybe<string>;

  /**
   * only applicable for external department
   */
  @Field(type => String)
  externalDepartmentDepartment: Maybe<string>;

  /**
   * only applicable for others department
   */
  @Field(type => String)
  otherDepartmentFurtherInformation: Maybe<string>;

  @validate(nonOptional())
  @Field(type => IKHOccupationEnum)
  occupation: Maybe<IKHOccupationEnum>;

  @Field(type => String)
  hessHochschulDepartment: Maybe<string>;

  @validate(nonOptional())
  @Field(type => IKHInvoiceReceiverEnum, {
    dynamicValidator: 'non_optional',
    default: v => v ?? IKHInvoiceReceiverEnum.IKHPRIVATE,
  })
  invoiceReceiver: Maybe<IKHInvoiceReceiverEnum>;

  /**
   * only applicable for external invoice receiver
   */
  @validate(({ value, target, key }) => {
    if (target.invoiceReceiver === IKHInvoiceReceiverEnum.IKHEXTERNAL) return nonOptional()({ value, target, key });
  })
  @Field(type => String)
  institution: Maybe<string>;

  /**
   * only applicable for external invoice receiver
   */
  @validate(({ value, target, key }) => {
    if (target.invoiceReceiver === IKHInvoiceReceiverEnum.IKHEXTERNAL) return nonOptional()({ value, target, key });
  })
  @Field(type => String)
  institutionEmail: Maybe<string>;

  /**
   * only applicable for internal invoice receiver
   */
  @Field(type => String)
  division: Maybe<string>;

  /**
   * only applicable for internal invoice receiver
   */
  @Field(type => String)
  postbox: Maybe<string>;

  /**
   * - only applicable for internal invoice receiver
   * - non optional
   */
  @validate(({ value, target, key }) => {
    if (target.invoiceReceiver === IKHInvoiceReceiverEnum.IKHINTERNAL)
      return nonOptional({ allowFalsy: true })({ value, target, key });
  })
  @Field(type => String)
  costCenter: Maybe<string>;

  @Field(type => String)
  attention: Maybe<string>;

  @validate(({ value, target, key }) => {
    if (target.invoiceReceiver === IKHInvoiceReceiverEnum.IKHINTERNAL) return;
    const errorFields = (
      !target.address
        ? [{ path: ['address'], target }]
        : [
            !target.address?.streetAndHousenumber
              ? { path: ['address', 'streetAndHousenumber'], target: target.address }
              : null,
            ...(!target.address.zipCity
              ? [{ path: ['address', 'zipCity'], target: target.address }]
              : [
                  !target.address?.zipCity?.zip
                    ? { path: ['address', 'zipCity', 'zip'], target: target.address?.zipCity }
                    : null,
                  !target.address?.zipCity?.city
                    ? { path: ['address', 'zipCity', 'city'], target: target.address?.zipCity }
                    : null,
                ]),
          ]
    ).filter(Boolean) as Array<{ path: string[]; target: any }>;

    if (errorFields.length)
      throw new MultipleValidationError(
        errorFields.map(
          ({ path, target }) =>
            new ValidationError(`Key ${path.slice(-1)[0]} is required!`, {
              key: path.slice(-1)[0],
              translationKey: 'validation_non_optional',
              path: path.slice(0, -1),
              target,
            }),
        ),
      );
  })
  @Field(type => Address, { default: v => v ?? new Address({}) })
  address: Maybe<Address>;
}
